function wait(ms) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

export default class ProjectFilter {
    constructor() {
        const bar = document.querySelector('.js-work__filter-bar');

        if (!bar) return;

        this.bar = bar;
        this.track = bar.querySelector('.js-work__filter-track');
        this.list = document.querySelector('.js-work__list');
        this.pagination = document.querySelector('.js-work__pagination');
        this.buttons = [...bar.querySelectorAll('.js-work__filter')];

        this.cfg = window.quadrantWork;

        if (!this.cfg || !this.cfg.ajaxUrl) return;

        const active = this.buttons.find((b) => b.classList.contains('is-active'));
        this.strategy = active?.dataset.workFilter || 'all';
        this.controller = null;
        this.fadeFrame = null;

        // Grid/list is appearance-only: the toggle re-fetches the current view so
        // the server renders the matching partial. Not in the URL - reloads reset to grid.
        this.modeToggles = [...document.querySelectorAll('.js-work__mode-toggle')];
        this.layout = this.list?.dataset.layout === 'list' ? 'list' : 'grid';

        const pagedMatch = window.location.pathname.match(/\/page\/(\d+)/);
        this.paged = pagedMatch ? parseInt(pagedMatch[1], 10) : 1;

        this.buttons.forEach((btn) => {
            btn.addEventListener('click', () => {
                const { workFilter: strategy } = btn.dataset;
                if (strategy === this.strategy) return;
                this.load(strategy, 1);
            });
        });

        this.modeToggles.forEach((btn) => {
            btn.addEventListener('click', () => {
                const { mode: layout } = btn.dataset;
                if (!layout || layout === this.layout) return;
                // Page sizes differ per mode (grid 8, list 10), so the current page
                // number doesn't carry over - always restart at page 1. The mode
                // itself isn't in the URL, so only push when the page changed.
                this.load(this.strategy, 1, { layout, push: this.paged !== 1 });
            });
        });

        this.pagination?.addEventListener('click', (e) => this.onPaginationClick(e));

        this.track?.addEventListener('scroll', () => {
            if (this.fadeFrame) return;
            this.fadeFrame = requestAnimationFrame(() => {
                this.fadeFrame = null;
                this.updateFades();
            });
        });

        window.addEventListener('resize', () => this.updateFades());
        window.addEventListener('popstate', (e) => this.onPopState(e));

        this.updateFades();
    }

    onPaginationClick(e) {
        const link = e.target.closest('a.page-numbers');

        if (!link || link.classList.contains('current') || link.classList.contains('dots')) return;

        e.preventDefault();

        const href = link.getAttribute('href') || '';
        const match = href.match(/\/page\/(\d+)/) || href.match(/[?&]paged=(\d+)/);
        const page = match ? parseInt(match[1], 10) : 1;

        // Move/scroll focus only once the new page has actually swapped in.
        this.load(this.strategy, page, { onSuccess: () => this.focusList() });
    }

    load(strategy, paged, { push = true, onSuccess, layout = this.layout } = {}) {
        const previous = this.strategy;
        const previousLayout = this.layout;

        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        const { controller } = this;

        // Reflect the selection immediately. Read the fade duration BEFORE dimming
        // (0 under reduced motion = instant swap), then set loading up front.
        this.setActive(strategy);
        this.setLayoutActive(layout);

        const fadeMs = this.list
            ? parseFloat(getComputedStyle(this.list).transitionDuration) * 1000 || 0
            : 0;

        this.list?.setAttribute('aria-busy', 'true');
        this.list?.classList.add('is-loading');

        const request = fetch(this.cfg.ajaxUrl, {
            method: 'POST',
            body: new URLSearchParams({
                action: 'work_filter',
                nonce: this.cfg.nonce,
                strategy,
                paged,
                layout,
            }),
            signal: controller.signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        }).then((r) => r.json());

        // Hold the swap until both the fade-out and the data are ready, so content
        // never changes mid-transition.
        return Promise.all([request, wait(fadeMs)])
            .then(([data]) => {
                if (controller !== this.controller) return; // superseded by a newer request
                if (!data || !data.success) {
                    this.setActive(previous);
                    this.setLayoutActive(previousLayout);
                    return;
                }

                if (this.list) {
                    this.list.innerHTML = data.data.projects;
                    this.list.dataset.layout = layout;
                }
                if (this.pagination) this.pagination.innerHTML = data.data.pagination;
                this.strategy = strategy;
                this.layout = layout;
                this.paged = paged;

                if (push) this.pushUrl(strategy, paged);
                if (onSuccess) onSuccess();
            })
            .catch((err) => {
                if (err.name === 'AbortError') return;
                if (controller === this.controller) {
                    this.setActive(previous);
                    this.setLayoutActive(previousLayout);
                }
                console.error(err);
            })
            .finally(() => {
                if (controller !== this.controller) return;
                this.list?.classList.remove('is-loading');
                this.list?.setAttribute('aria-busy', 'false');
            });
    }

    setActive(strategy) {
        this.buttons.forEach((b) => {
            const on = b.dataset.workFilter === strategy;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
    }

    setLayoutActive(layout) {
        this.modeToggles.forEach((b) => {
            const on = b.dataset.mode === layout;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
    }

    focusList() {
        if (!this.list) return;

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.list.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        this.list.focus({ preventScroll: true });
    }

    pushUrl(strategy, paged) {
        // Mirror the server's pretty pagination links (/work/page/N/?strategy=ID)
        // so a reload or back/forward restores the same view the AJAX showed.
        const base = window.location.pathname.replace(/\/page\/\d+\/?$/, '/');
        const path = paged > 1 ? `${base}page/${paged}/` : base;
        const params = new URLSearchParams();

        if (strategy && strategy !== 'all') params.set('strategy', strategy);

        const query = params.toString();

        window.history.pushState({ strategy, paged }, '', query ? `${path}?${query}` : path);
    }

    onPopState(e) {
        if (e.state) {
            this.load(e.state.strategy || 'all', e.state.paged || 1, { push: false });
            return;
        }

        // Initial history entry (nothing pushed): read state back off the URL.
        const match = window.location.pathname.match(/\/page\/(\d+)/);
        const paged = match ? parseInt(match[1], 10) : 1;
        const strategy = new URLSearchParams(window.location.search).get('strategy') || 'all';

        this.load(strategy, paged, { push: false });
    }

    updateFades() {
        if (!this.track) return;

        const max = this.track.scrollWidth - this.track.clientWidth;

        this.bar.classList.toggle('is-fade-start', this.track.scrollLeft > 1);
        this.bar.classList.toggle('is-fade-end', max > 1 && this.track.scrollLeft < max - 1);
    }
}
